import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    CalendarClock, DollarSign, GraduationCap, Inbox, LayoutDashboard, Mail, MailCheck, Megaphone, MessageSquare, Palette, Plus,
    ShoppingCart, Store, Ticket, Trash2, Users,
} from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, FieldRow, PageHead, Pill, Section, inputCls, cellCls } from "@/components/account/Shell";
import { Cell, Dash, Filters, IconBtn, PillRow, Sheet, Tag, Tiles, Two, Txt } from "@/components/account/Sheet";
import EmailDelivery from "@/components/admin/EmailDelivery";
import MarketplaceAdmin from "@/components/admin/MarketplaceAdmin";
import BillboardsAdmin from "@/components/admin/BillboardsAdmin";
import CouponsAdmin from "@/components/admin/CouponsAdmin";
import ShopsAdmin from "@/components/admin/ShopsAdmin";
import NotificationsAdmin from "@/components/admin/NotificationsAdmin";
import PayoutsAdmin from "@/components/admin/PayoutsAdmin";
import CallsAdmin from "@/components/admin/CallsAdmin";
import SupportAdmin from "@/components/admin/SupportAdmin";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { HOUSE_SLUG } from "@/lib/mall";
import { when } from "@/lib/when";

const STATUSES = ["pending_payment", "paid", "in_production", "ready", "completed", "cancelled"];
const LABELS = { pending_payment: "Awaiting payment", paid: "Paid", in_production: "In production", ready: "Ready", completed: "Completed", cancelled: "Cancelled" };
const TABS = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "orders", label: "Orders", icon: ShoppingCart },
    { key: "shops", label: "Shops", icon: Store },
    { key: "calls", label: "Calls", icon: CalendarClock },
    { key: "livechat", label: "Live chat", icon: MessageSquare },
    { key: "payouts", label: "Payouts", icon: DollarSign },
    { key: "notifications", label: "Notifications", icon: Inbox },
    { key: "customers", label: "Customers", icon: Users },
    { key: "artwork", label: "Artwork", icon: Palette },
    { key: "leads", label: "Leads", icon: Mail },
    { key: "classes", label: "Classes", icon: GraduationCap },
    { key: "email", label: "Email", icon: MailCheck },
    { key: "marketplace", label: "Marketplace", icon: Store },
    { key: "billboards", label: "Billboards", icon: Megaphone },
    { key: "coupons", label: "Coupons", icon: Ticket },
];
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;


const ORDER_COLS = [
    { key: "num", label: "Order #", sub: "the number on the receipt", w: "112px" },
    { key: "when", label: "Placed", sub: "date · how long ago", w: "150px" },
    { key: "customer", label: "Customer", sub: "name · phone", w: "minmax(170px,1.2fr)" },
    { key: "shops", label: "Shops on it", sub: "who fulfils the lines", w: "minmax(140px,1fr)" },
    { key: "lines", label: "Lines", sub: "product — what they chose · artwork", w: "minmax(260px,2.2fr)" },
    { key: "total", label: "Total", sub: "$ whole order", w: "92px" },
    { key: "status", label: "Status", sub: "change it here", w: "160px" },
];
const CUSTOMER_COLS = [
    { key: "name", label: "Name", w: "minmax(180px,1.2fr)" },
    { key: "email", label: "Email", w: "minmax(220px,1.4fr)" },
    { key: "phone", label: "Phone", w: "150px" },
    { key: "orders", label: "Orders", sub: "placed all time", w: "90px" },
];
const ART_COLS = [
    { key: "when", label: "Received", sub: "date · how long ago", w: "150px" },
    { key: "who", label: "Who", sub: "name · phone · email", w: "minmax(200px,1.2fr)" },
    { key: "what", label: "What they need", sub: "their words", w: "minmax(280px,2.4fr)" },
    { key: "style", label: "Style", w: "130px" },
    { key: "needed", label: "Needed by", w: "110px" },
    { key: "files", label: "Files", sub: "click to open", w: "minmax(150px,1fr)" },
];
const LEAD_KINDS = [
    { key: "all", label: "Everything" },
    { key: "lead", label: "Website & general" },
    { key: "wholesale", label: "Wholesale signups" },
];
const LEAD_COLS = [
    { key: "when", label: "Received", sub: "date · how long ago", w: "150px" },
    { key: "kind", label: "Kind", w: "128px" },
    { key: "who", label: "Who", sub: "name · business", w: "minmax(180px,1.2fr)" },
    { key: "contact", label: "Contact", sub: "email · phone", w: "minmax(220px,1.4fr)" },
    { key: "topic", label: "Topic", w: "140px" },
    { key: "message", label: "Message", w: "minmax(260px,2.2fr)" },
];
const CLASS_COLS = [
    { key: "title", label: "Class", w: "minmax(220px,1.6fr)" },
    { key: "date", label: "Date", w: "120px" },
    { key: "time", label: "Time", w: "110px" },
    { key: "price", label: "Price", sub: "$ per person", w: "90px" },
    { key: "seats", label: "Seats", w: "80px" },
    { key: "actions", label: "Actions", sub: "delete", w: "80px" },
];
const REG_COLS = [
    { key: "when", label: "Signed up", sub: "date · how long ago", w: "150px" },
    { key: "name", label: "Who signed up", w: "minmax(180px,1fr)" },
    { key: "email", label: "Email", w: "minmax(220px,1.2fr)" },
    { key: "class", label: "Class", w: "minmax(220px,1.4fr)" },
];
const BLANK_CLASS = { title: "", description: "", date: "", time: "", price: "", seats: "", image: "https://13thav.com/shirts/classes.jpg" };

export default function Admin() {
    const [tab, setTab] = useState("overview");
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [shops, setShops] = useState([]);
    const [byShop, setByShop] = useState({});
    const [shopFilter, setShopFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [makerFilter, setMakerFilter] = useState("all");
    const [customers, setCustomers] = useState([]);
    const [artwork, setArtwork] = useState([]);
    const [leads, setLeads] = useState({ leads: [], wholesale: [] });
    const [leadKind, setLeadKind] = useState("all");
    const [classes, setClasses] = useState([]);
    const [regs, setRegs] = useState([]);
    const [newClass, setNewClass] = useState(BLANK_CLASS);

    const load = () => {
        api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
        api.get("/admin/orders").then(({ data }) => { setOrders(data.orders); setShops(data.shops || []); setByShop(data.by_shop || {}); }).catch(() => {});
        api.get("/admin/customers").then(({ data }) => setCustomers(data.customers)).catch(() => {});
        api.get("/admin/artwork-requests").then(({ data }) => setArtwork(data.requests)).catch(() => {});
        api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
        api.get("/classes").then(({ data }) => setClasses(data.classes)).catch(() => {});
        api.get("/admin/class-registrations").then(({ data }) => setRegs(data.registrations)).catch(() => {});
    };
    useEffect(load, []);

    const pickShop = (slug) => {
        setShopFilter(slug);
        api.get("/admin/orders", { params: slug ? { store: slug } : {} }).then(({ data }) => setOrders(data.orders));
    };
    const setStatus = async (id, status) => {
        try { await api.patch(`/admin/orders/${id}`, { status }); setOrders((o) => o.map((x) => x.id === id ? { ...x, status } : x)); toast.success("Updated"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const createClass = async () => {
        try { await api.post("/admin/classes", { ...newClass, price: +newClass.price, seats: +newClass.seats }); toast.success("Class added"); setNewClass(BLANK_CLASS); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const delClass = async (id) => { await api.delete(`/admin/classes/${id}`); load(); };
    const setC = (k) => (e) => setNewClass({ ...newClass, [k]: e.target.value });

    const isMine = (o) => (o.items || []).some((i) => (i.store_slug || HOUSE_SLUG) === HOUSE_SLUG);
    const byMaker = makerFilter === "mine" ? orders.filter(isMine) : makerFilter === "others" ? orders.filter((o) => !isMine(o)) : orders;
    const shownOrders = statusFilter === "all" ? byMaker : byMaker.filter((o) => o.status === statusFilter);
    const leadRows = [
        ...leads.leads.map((l) => ({ ...l, kind: "lead" })),
        ...leads.wholesale.map((w) => ({ ...w, kind: "wholesale" })),
    ].filter((r) => leadKind === "all" || r.kind === leadKind);

    return (
        <BackOffice>
            <PageHead kicker="Platform admin" title="Dashboard" testid="admin-head"
                sub="Every shop, order, message and approval on 13thav — one tab per job, one row per record." />

            <div className="mt-3 flex flex-wrap gap-1" data-testid="admin-tabs">
                {TABS.map((t) => (
                    <Pill key={t.key} active={tab === t.key} onClick={() => setTab(t.key)} testid={`admin-tab-${t.key}`}>
                        <t.icon size={12} /> {t.label}
                    </Pill>
                ))}
            </div>

            <div className="mt-3">
                {tab === "overview" && stats && (
                    <div data-testid="admin-overview">
                        <Tiles items={[
                            ["Orders", stats.orders, "placed all time"],
                            ["Revenue", `$${stats.revenue.toFixed(0)}`, "booked across every shop"],
                            ["Customers", stats.customers, "accounts that ordered"],
                            ["Artwork requests", stats.artwork_requests, "files to make or fix"],
                            ["Quotes", stats.quotes, "website & general leads"],
                            ["Class signups", stats.class_registrations, "seats booked"],
                        ]} />
                    </div>
                )}

                {tab === "email" && <EmailDelivery />}
                {tab === "marketplace" && <MarketplaceAdmin />}
                {tab === "billboards" && <BillboardsAdmin />}
                {tab === "coupons" && <CouponsAdmin />}
                {tab === "shops" && <ShopsAdmin />}
                {tab === "payouts" && <PayoutsAdmin />}
                {tab === "notifications" && <NotificationsAdmin />}
                {tab === "calls" && <CallsAdmin />}
                {tab === "livechat" && <SupportAdmin />}

                {tab === "orders" && (
                    <div data-testid="admin-orders">
                        <Filters testid="admin-orders-filters">
                            <PillRow label="Shop" testid="orders-shop" value={shopFilter} onPick={pickShop}
                                choices={[{ key: "", label: "Every shop", count: orders.length }, ...shops.map((s) => ({ key: s.slug, label: s.name, count: byShop[s.slug] || 0 }))]} />
                            <PillRow label="Who makes it" testid="orders-maker" value={makerFilter} onPick={setMakerFilter}
                                choices={[{ key: "all", label: "Everything", count: orders.length },
                                    { key: "mine", label: "Mine to make", count: orders.filter(isMine).length },
                                    { key: "others", label: "Other shops", count: orders.filter((o) => !isMine(o)).length }]} />
                            <PillRow label="Status" testid="orders-status" value={statusFilter} onPick={setStatusFilter}
                                choices={[{ key: "all", label: "Every status", count: byMaker.length },
                                    ...STATUSES.map((s) => ({ key: s, label: LABELS[s], count: byMaker.filter((o) => o.status === s).length }))]} />
                        </Filters>
                        <div className="mt-3">
                            <Section title={`${shownOrders.length} orders`} pad={false} testid="admin-orders-sheet"
                                note="newest first · change a status right in the row">
                                <Sheet cols={ORDER_COLS} rows={shownOrders} rowKey={(o) => o.id} testid="orders-sheet" minWidth={1120}
                                    empty="No orders match those filters." rowTestid={(o) => `admin-order-${o.order_number}`}
                                    cells={(o) => ({
                                        num: <Cell><Txt bold mono>{o.order_number}</Txt></Cell>,
                                        when: <Cell><Txt sub>{when(o.created_at)}</Txt></Cell>,
                                        customer: <Cell><Two a={o.contact?.name || "Walk-in"} b={[o.contact?.phone, o.contact?.email].filter(Boolean).join(" · ")} /></Cell>,
                                        shops: <Cell><Txt testid={`order-shops-${o.order_number}`}>{(o.stores || []).map((s) => s.name).join(" · ") || "13th Ave"}</Txt></Cell>,
                                        lines: (
                                            <Cell className="py-1">
                                                <div className="min-w-0 space-y-0.5">
                                                    {o.items.map((it, i) => (
                                                        <p key={i} className="truncate text-[12px] text-[#1a1a1a]" title={`${it.product_name} — ${it.summary}`}>
                                                            <span className="font-semibold">{it.product_name}</span>
                                                            {it.summary ? <span className="text-[#4a4a4a]"> — {it.summary}</span> : null}
                                                            {(it.artwork || []).map((a, j) => (
                                                                <a key={a.id || j} href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="ml-1.5 text-[#1a56db] underline">{a.filename}</a>
                                                            ))}
                                                        </p>
                                                    ))}
                                                    {o.notes && <p className="truncate text-[11px] italic text-[#4a4a4a]" title={o.notes}>“{o.notes}”</p>}
                                                </div>
                                            </Cell>
                                        ),
                                        total: <Cell><Txt bold mono right>{money(o.total)}</Txt></Cell>,
                                        status: (
                                            <Cell>
                                                <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} data-testid={`order-status-${o.order_number}`}
                                                    className={`${cellCls} w-full py-px text-left text-[11px] font-semibold`}>
                                                    {STATUSES.map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
                                                </select>
                                            </Cell>
                                        ),
                                    })} />
                            </Section>
                        </div>
                    </div>
                )}

                {tab === "customers" && (
                    <Section title={`${customers.length} customers`} pad={false} testid="admin-customers" note="everyone who has placed an order">
                        <Sheet cols={CUSTOMER_COLS} rows={customers} rowKey={(c) => c.id} testid="customers-sheet" minWidth={760}
                            empty="No customers yet." rowTestid={(c) => `admin-customer-${c.id}`}
                            cells={(c) => ({
                                name: <Cell><Txt bold>{c.name}</Txt></Cell>,
                                email: <Cell><Txt>{c.email}</Txt></Cell>,
                                phone: <Cell>{c.phone ? <Txt>{c.phone}</Txt> : <Dash why="No phone on file" />}</Cell>,
                                orders: <Cell><Txt mono right>{c.order_count}</Txt></Cell>,
                            })} />
                    </Section>
                )}

                {tab === "artwork" && (
                    <Section title={`${artwork.length} artwork requests`} pad={false} testid="admin-artwork" note="files to make, fix or recreate">
                        <Sheet cols={ART_COLS} rows={artwork} rowKey={(a) => a.id} testid="artwork-sheet" minWidth={1040}
                            empty="No artwork requests." rowTestid={(a) => `admin-art-${a.id}`}
                            cells={(a) => ({
                                when: <Cell><Txt sub>{when(a.created_at)}</Txt></Cell>,
                                who: <Cell><Two a={a.name} b={[a.phone, a.email].filter(Boolean).join(" · ")} /></Cell>,
                                what: <Cell><Txt title={a.description}>{a.description}</Txt></Cell>,
                                style: <Cell>{a.style_preference ? <Txt>{a.style_preference}</Txt> : <Dash why="No style given" />}</Cell>,
                                needed: <Cell>{a.date_needed ? <Txt>{a.date_needed}</Txt> : <Dash why="No date given" />}</Cell>,
                                files: (
                                    <Cell className="flex-wrap gap-x-2">
                                        {(a.files || []).length ? (a.files || []).map((f, i) => (
                                            <a key={f.id || i} href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="truncate text-[11px] font-semibold text-[#1a56db] underline">{f.filename}</a>
                                        )) : <Dash why="No files attached" />}
                                    </Cell>
                                ),
                            })} />
                    </Section>
                )}

                {tab === "leads" && (
                    <div data-testid="admin-leads">
                        <Filters testid="admin-leads-filters">
                            <PillRow label="Kind" testid="leads-kind" value={leadKind} onPick={setLeadKind}
                                choices={LEAD_KINDS.map((k) => ({ ...k, count: k.key === "all" ? leads.leads.length + leads.wholesale.length : k.key === "lead" ? leads.leads.length : leads.wholesale.length }))} />
                        </Filters>
                        <div className="mt-3">
                            <Section title={`${leadRows.length} leads`} pad={false} testid="admin-leads-sheet" note="website quotes, general questions and wholesale signups">
                                <Sheet cols={LEAD_COLS} rows={leadRows} rowKey={(l) => `${l.kind}-${l.id}`} testid="leads-sheet" minWidth={1000}
                                    empty="None yet." rowTestid={(l) => `admin-lead-${l.id}`}
                                    cells={(l) => ({
                                        when: <Cell><Txt sub>{when(l.created_at)}</Txt></Cell>,
                                        kind: <Cell><Tag status={l.kind === "wholesale" ? "active" : "booked"}>{l.kind === "wholesale" ? "wholesale" : "website"}</Tag></Cell>,
                                        who: <Cell><Two a={l.name} b={l.business_name} /></Cell>,
                                        contact: <Cell><Txt>{[l.email, l.phone].filter(Boolean).join(" · ")}</Txt></Cell>,
                                        topic: <Cell>{l.topic ? <Txt>{l.topic}</Txt> : <Dash why="No topic" />}</Cell>,
                                        message: <Cell>{l.message ? <Txt title={l.message}>{l.message}</Txt> : <Dash why="No message" />}</Cell>,
                                    })} />
                            </Section>
                        </div>
                    </div>
                )}

                {tab === "classes" && (
                    <div className="space-y-3" data-testid="admin-classes">
                        <Section title={`${classes.length} classes`} pad={false} testid="admin-classes-sheet" note="what's on the public classes board">
                            <Sheet cols={CLASS_COLS} rows={classes} rowKey={(c) => c.id} testid="classes-sheet" minWidth={760}
                                empty="No classes on the board." rowTestid={(c) => `admin-class-${c.id}`}
                                cells={(c) => ({
                                    title: <Cell><Txt bold>{c.title}</Txt></Cell>,
                                    date: <Cell><Txt>{c.date}</Txt></Cell>,
                                    time: <Cell><Txt>{c.time}</Txt></Cell>,
                                    price: <Cell><Txt mono right>{money(c.price)}</Txt></Cell>,
                                    seats: <Cell><Txt mono right>{c.seats}</Txt></Cell>,
                                    actions: <Cell className="justify-end"><IconBtn onClick={() => delClass(c.id)} title="Delete this class" danger testid={`admin-class-delete-${c.id}`}><Trash2 size={12} /></IconBtn></Cell>,
                                })} />
                        </Section>

                        <Section title={`${regs.length} registrations`} pad={false} testid="admin-registrations" note="seats booked from the classes page">
                            <Sheet cols={REG_COLS} rows={regs} rowKey={(r) => r.id} testid="registrations-sheet" minWidth={700}
                                empty="Nobody has signed up yet."
                                cells={(r) => ({
                                    when: <Cell><Txt sub>{when(r.created_at)}</Txt></Cell>,
                                    name: <Cell><Txt bold>{r.name}</Txt></Cell>,
                                    email: <Cell><Txt>{r.email}</Txt></Cell>,
                                    class: <Cell><Txt>{r.class_title}</Txt></Cell>,
                                })} />
                        </Section>

                        <Section title="Add a class" pad={false} testid="admin-class-form" note="one field per line · lands on the board the moment you add it">
                            <FieldRow label="Title" hint="what the class is called on the board">
                                <input className={inputCls} placeholder="Custom Garment Printing Workshop" value={newClass.title} onChange={setC("title")} data-testid="class-title-input" />
                            </FieldRow>
                            <FieldRow label="Description" hint="what they'll learn, in a sentence or two" top>
                                <textarea className={inputCls} rows={2} placeholder="Two hours on the press: art prep, heat, pressure, time." value={newClass.description} onChange={setC("description")} data-testid="class-description-input" />
                            </FieldRow>
                            <FieldRow label="Date">
                                <input className={`${inputCls} max-w-[220px]`} type="date" value={newClass.date} onChange={setC("date")} data-testid="class-date-input" />
                            </FieldRow>
                            <FieldRow label="Time" hint="as you'd say it — 1:00 PM">
                                <input className={`${inputCls} max-w-[220px]`} placeholder="1:00 PM" value={newClass.time} onChange={setC("time")} data-testid="class-time-input" />
                            </FieldRow>
                            <FieldRow label="Price per person" hint="$ per seat">
                                <input className={`${inputCls} max-w-[160px]`} type="number" min="0" placeholder="99" value={newClass.price} onChange={setC("price")} data-testid="class-price-input" />
                            </FieldRow>
                            <FieldRow label="Seats" hint="how many people fit">
                                <input className={`${inputCls} max-w-[160px]`} type="number" min="1" placeholder="3" value={newClass.seats} onChange={setC("seats")} data-testid="class-seats-input" />
                            </FieldRow>
                            <div className="border-t border-[#e5e5e5] px-2.5 py-2">
                                <Btn tone="solid" onClick={createClass} testid="add-class-button"><Plus size={11} /> Add class</Btn>
                            </div>
                        </Section>
                    </div>
                )}
            </div>
        </BackOffice>
    );
}
